import {
  deleteToken,
  getMessaging,
  getToken,
  isSupported,
  onMessage,
  type MessagePayload
} from 'firebase/messaging';
import { firebaseApp } from '../firebase';

export type BrowserPushStatus =
  | 'unsupported'
  | 'missing_config'
  | 'disabled'
  | 'blocked'
  | 'enabled'
  | 'error';

export interface BrowserPushState {
  status: BrowserPushStatus;
  subscriptionId: string | null;
  token: string | null;
  label: string;
  detail: string;
}

export interface BrowserPushMessage {
  title: string;
  body: string;
  link: string;
}

const SW_URL = '/firebase-messaging-sw.js';
const PUSH_OPT_OUT_KEY = 'slugnet.browser_push.disabled.v1';

function vapidKey() {
  return String(import.meta.env.VITE_FIREBASE_VAPID_KEY || '').trim();
}

function defaultState(status: BrowserPushStatus, detail: string): BrowserPushState {
  return {
    status,
    subscriptionId: null,
    token: null,
    label: currentDeviceLabel(),
    detail
  };
}

function readPushOptOut() {
  try {
    return window.localStorage.getItem(PUSH_OPT_OUT_KEY) === '1';
  } catch {
    return false;
  }
}

function writePushOptOut(disabled: boolean) {
  try {
    if (disabled) window.localStorage.setItem(PUSH_OPT_OUT_KEY, '1');
    else window.localStorage.removeItem(PUSH_OPT_OUT_KEY);
  } catch {
    // Ignore storage failures.
  }
}

function normalizeLink(input: string | undefined) {
  if (!input) return '/notifications';
  if (input.startsWith('/')) return input;
  try {
    const url = new URL(input);
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return '/notifications';
  }
}

function messageFromPayload(payload: MessagePayload): BrowserPushMessage {
  const data = payload.data || {};
  const title = String(data.title || payload.notification?.title || 'Discussion updated').trim() || 'Discussion updated';
  const body = String(data.body || payload.notification?.body || 'There is a new discussion update').trim();
  const link = normalizeLink(String(data.link || ''));
  return { title, body, link };
}

function currentDeviceLabel() {
  if (typeof navigator === 'undefined') return 'This browser';
  const ua = navigator.userAgent;
  const platform = navigator.platform || 'Browser';
  let browser = 'Browser';
  if (ua.includes('Edg/')) browser = 'Edge';
  else if (ua.includes('Chrome/')) browser = 'Chrome';
  else if (ua.includes('Firefox/')) browser = 'Firefox';
  else if (ua.includes('Safari/')) browser = 'Safari';
  return `${browser} on ${platform}`;
}

async function supported() {
  if (typeof window === 'undefined') return false;
  if (!window.isSecureContext) return false;
  if (!('serviceWorker' in navigator) || !('Notification' in window)) return false;
  return isSupported();
}

async function messagingInstance() {
  if (!(await supported())) return null;
  return getMessaging(firebaseApp);
}

async function ensureServiceWorker() {
  return navigator.serviceWorker.register(SW_URL, { type: 'module' });
}

async function tokenId(token: string) {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(token));
  return Array.from(new Uint8Array(digest))
    .map((value) => value.toString(16).padStart(2, '0'))
    .join('')
    .slice(0, 40);
}

async function resolveGrantedState(): Promise<BrowserPushState> {
  const messaging = await messagingInstance();
  const key = vapidKey();
  if (!messaging) return defaultState('unsupported', 'Browser notifications are not supported here.');
  if (!key) return defaultState('missing_config', 'Push notifications are not configured yet.');

  try {
    const registration = await ensureServiceWorker();
    const token = await getToken(messaging, {
      vapidKey: key,
      serviceWorkerRegistration: registration
    });

    if (!token) return defaultState('disabled', 'Browser notifications are not enabled on this device.');

    return {
      status: 'enabled',
      subscriptionId: await tokenId(token),
      token,
      label: currentDeviceLabel(),
      detail: 'Enabled on this browser'
    };
  } catch (error: any) {
    console.error('[browser-push] init failed', error);
    const message = String(error?.message || '');
    if (message.toLowerCase().includes('permission')) {
      return defaultState('blocked', 'Browser notification permission is blocked.');
    }
    return defaultState('error', 'Could not initialise browser notifications.');
  }
}

export async function getBrowserPushState(): Promise<BrowserPushState> {
  if (!(await supported())) return defaultState('unsupported', 'Browser notifications are not supported here.');
  if (!vapidKey()) return defaultState('missing_config', 'Push notifications are not configured yet.');

  if (Notification.permission === 'denied') {
    return defaultState('blocked', 'Browser notification permission is blocked.');
  }
  if (readPushOptOut()) {
    return defaultState('disabled', 'Browser notifications are not enabled on this device.');
  }
  if (Notification.permission !== 'granted') {
    return defaultState('disabled', 'Browser notifications are not enabled on this device.');
  }
  return resolveGrantedState();
}

export async function enableBrowserPush(): Promise<BrowserPushState> {
  if (!(await supported())) return defaultState('unsupported', 'Browser notifications are not supported here.');
  if (!vapidKey()) return defaultState('missing_config', 'Push notifications are not configured yet.');

  const permission = await Notification.requestPermission();
  if (permission === 'denied') return defaultState('blocked', 'Browser notification permission is blocked.');
  if (permission !== 'granted') return defaultState('disabled', 'Browser notifications are not enabled on this device.');
  writePushOptOut(false);
  return resolveGrantedState();
}

export async function disableBrowserPush(): Promise<BrowserPushState> {
  const current = Notification.permission === 'granted' ? await resolveGrantedState() : await getBrowserPushState();
  const messaging = await messagingInstance();
  if (!messaging || current.status !== 'enabled') {
    writePushOptOut(true);
    return defaultState('disabled', 'Browser notifications are not enabled on this device.');
  }

  try {
    await deleteToken(messaging);
    writePushOptOut(true);
    return {
      ...current,
      status: 'disabled',
      token: null,
      detail: 'Browser notifications are not enabled on this device.'
    };
  } catch {
    writePushOptOut(false);
    return defaultState('error', 'Could not disable browser notifications.');
  }
}

export async function subscribeForegroundPushMessages(
  onData: (message: BrowserPushMessage) => void
) {
  const messaging = await messagingInstance();
  if (!messaging) return () => undefined;
  return onMessage(messaging, (payload) => onData(messageFromPayload(payload)));
}

export function showForegroundBrowserNotification(message: BrowserPushMessage) {
  if (typeof window === 'undefined') return;
  if (!('Notification' in window) || Notification.permission !== 'granted') return;
  const notification = new Notification(message.title, {
    body: message.body,
    icon: '/push-icon.png',
    data: { link: message.link }
  });
  notification.onclick = () => {
    window.focus();
    window.location.assign(message.link);
    notification.close();
  };
}
